import { atom } from 'jotai';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

// Helper function to get system preference
const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

// Helper function to get stored theme from localStorage
const getStoredTheme = (): Theme => {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = localStorage.getItem('theme') as Theme;
    return stored && ['light', 'dark', 'system'].includes(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
};

// Helper function to resolve theme to actual light/dark value
const resolveTheme = (theme: Theme): ResolvedTheme => {
  if (theme === 'system') {
    return getSystemTheme();
  }
  return theme;
};

// Base theme atom
export const themeAtom = atom<Theme>(getStoredTheme());

// Resolved theme atom (computed)
export const resolvedThemeAtom = atom<ResolvedTheme>((get) => {
  const theme = get(themeAtom);
  return resolveTheme(theme);
});

// System theme atom (tracks system preference changes)
export const systemThemeAtom = atom<ResolvedTheme>(getSystemTheme());

// Theme setter atom
export const setThemeAtom = atom(
  null,
  (_get, set, newTheme: Theme) => {
    // Update the theme atom
    set(themeAtom, newTheme);
    
    // Persist to localStorage
    try {
      localStorage.setItem('theme', newTheme);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
    
    // Apply theme to document
    const resolvedTheme = resolveTheme(newTheme);
    applyThemeToDocument(resolvedTheme);
  }
);

// Function to apply theme to document
export const applyThemeToDocument = (theme: ResolvedTheme) => {
  if (typeof document === 'undefined') return;
  
  const root = document.documentElement;
  
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
};

// Function to initialize theme on app startup
export const initializeTheme = () => {
  if (typeof window === 'undefined') return;
  
  const storedTheme = getStoredTheme();
  const resolvedTheme = resolveTheme(storedTheme);
  
  // Apply theme immediately to prevent flash
  applyThemeToDocument(resolvedTheme);
  
  // Set up system theme change listener
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handleSystemThemeChange = (e: MediaQueryListEvent) => {
    // This will be handled by the useTheme hook
    const newSystemTheme = e.matches ? 'dark' : 'light';
    
    // Dispatch a custom event that the hook can listen to
    window.dispatchEvent(new CustomEvent('system-theme-change', { 
      detail: { theme: newSystemTheme } 
    }));
  };
  
  // Add listener for system theme changes
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', handleSystemThemeChange);
  } else {
    // Fallback for older browsers
    mediaQuery.addListener(handleSystemThemeChange);
  }
  
  return () => {
    if (mediaQuery.removeEventListener) {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    } else {
      mediaQuery.removeListener(handleSystemThemeChange);
    }
  };
}; 